import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { User, UserChanges, UserGroups } from '@shared/models/user/user';
import { Site } from 'app/shared/models/user/site';
import { Region } from '@shared/models/region';
import { Company } from 'app/core/models/company';
import { Langue } from 'app/core/models/langue';
import { UserService } from '../../services/user-service';

interface UserFormData {
    userId?: string;
    firstName: string;
    lastName: string;
    email: string;
    language: string;
    langueId: number;
    role: string[];
    companyName: string;
    mainOrganization: string;
    secondaryOrganizations: string[];
    areas: number[];
    adminAreas: boolean;
    sites: number[];
    adminSites: boolean;
    enabled: boolean;
}

@Component({
    selector: 'app-new-user-modal',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        NgSelectModule
    ],
    templateUrl: './new-user-modal.component.html',
    styleUrls: ['./new-user-modal.component.scss'],
})

export class NewUserModalComponent implements OnInit {
    userForm: FormGroup;
    users: User[] = [];
    languages: Langue[] = [];
    roles: string[] = [];
    companies: Company[] = [];
    organizations: string[] = [];
    areas: Region[] = [];
    sites: Site[] = [];
    filteredSites: Site[] = [];
    isLoading = false;
    errorMessage: string | null = null;
    isEdit: boolean = false;
    selectedSiteId: number = null;
    selectedRegionId: number = null;

    constructor(
        public dialogRef: MatDialogRef<NewUserModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: FormBuilder,
        private userService: UserService,
        private translate: TranslateService
    ) {
        this.isEdit = !!this.data?.user;
        this.selectedSiteId = this.data?.selectedSiteId;
        this.selectedRegionId = this.data?.selectedRegionId;
        this.userForm = this.fb.group({
            firstName: ['', [Validators.required, Validators.minLength(2)]],
            lastName: ['', [Validators.required, Validators.minLength(2)]],
            email: ['', [Validators.required, Validators.email]],
            language: [null, Validators.required],
            role: [[], Validators.required],
            companyName: [null, Validators.required],
            mainOrganization: [{ value: '', disabled: true }],
            secondaryOrganizations: [{ value: [], disabled: true }],
            areas: [[], Validators.required],
            adminAreas: [false],
            sites: [[], Validators.required],
            adminSites: [false],
            enabled: [true]
        });
    }

    async ngOnInit(): Promise<void> {
        try {
            this.userService.getLanguages().subscribe((langs: Langue[]) => {
                this.languages = langs;
            });

            this.userService.getVertexGroups().subscribe((group: UserGroups) => {
                    const allGroups = [
                        ...(group.adminGroups || []),
                        ...(group.expertabGroups || []),
                        ...(group.exploreGroups || []),
                        
                    ];
                    this.roles = [...new Set(allGroups)];
            });

            this.userService.getAllCompanies().subscribe((comps: Company[]) => {
                this.companies = comps;
            });

            this.organizations = [];

            this.userService.getRegionsByCurrentUser().subscribe((regs: Region[]) => {
                this.areas = regs;
            });

            this.userService.getAllSites().subscribe((siteList: Site[]) => {
                this.sites = siteList;
                this.filteredSites = [...this.sites];

                // if (this.isEdit && this.data?.user) {
                    this.patchFormWithUserData();
                // }
            });

            this.userForm.get('areas')?.valueChanges.subscribe((selectedRegionIds: number[]) => {
                this.filterSitesByRegion(selectedRegionIds);
            });

            this.languages.sort((a, b) => a.nom.localeCompare(b.nom));
            this.companies.sort((a, b) => a.name.localeCompare(b.name));
            this.areas.sort((a, b) => a.nom.localeCompare(b.nom));
            this.sites.sort((a, b) => a.nom.localeCompare(b.nom));

        } catch (error) {
            console.error('Error initializing dropdown data:', error);
            this.errorMessage = this.translate.instant('SUBMISSION.INIT_ERROR');
        }
    }

    private patchFormWithUserData(): void {
        if (this.data?.user == null && this.selectedSiteId && this.selectedRegionId) {
            this.userForm.patchValue({
                areas: [this.selectedRegionId]
            });
            this.userForm.get('sites')?.setValue([this.selectedSiteId]);
            this.userForm.updateValueAndValidity();           
        }
        if (!this.data?.user) return;

        this.userService.getUserGroups(this.data.user.userId).subscribe((groups) => {
            const roles = groups.exploreGroups;
            const user = this.data.user as unknown as User;

            const formData: UserFormData = {
                userId: user.userId,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                language: this.languages.find(lang => lang.langueId === user.langueId)?.nom || '',
                langueId: user.langueId,
                role: roles || [],
                companyName: user.companyName,
                areas: user.areas.map(area => area.regionId) || [],
                adminAreas: false,
                sites: user.sites.map(site => site.siteId) || [],
                adminSites: false,
                enabled: user.enabled,
                mainOrganization: '',
                secondaryOrganizations: []
            };

            this.userForm.patchValue(formData);
        });


    }

    private formatLocalDateTime(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }

    filterSitesByRegion(selectedRegionIds: number[]) {
        if (selectedRegionIds && selectedRegionIds.length > 0) {
            this.filteredSites = this.sites.filter(site =>
                selectedRegionIds.includes(site.regionId)
            );
        } else {
            this.filteredSites = [...this.sites];
        }

        const currentSites = this.userForm.get('sites')?.value || [];
        if (currentSites.length > 0) {
            const validSites = currentSites.filter((site: string) =>
                this.filteredSites.some(fs => fs.nom === site)
            );
            this.userForm.get('sites')?.setValue(validSites);
        }
    }

    async onSubmit(): Promise<void> {
        if (this.userForm.invalid) {
            this.userForm.markAllAsTouched();
            this.errorMessage = this.translate.instant('SUBMISSION.FORM_INVALID');
            return;
        }

        this.isLoading = true;
        this.errorMessage = null;

        try {
            const formData = this.userForm.value;

            const user: User = {
                userId: this.data?.user?.userId || undefined,
                companyId: this.companies.filter(c => c.name === formData.companyName)[0].id,
                companyName: formData.companyName,
                organizationIds: [],
                username: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                emailVerified: false,
                enabled: formData.enabled,
                licenceType: 'None',
                dateCreation: this.formatLocalDateTime(new Date()),
                langueIsoCode: this.languages.filter(l => l.nom === formData.language)[0].identifiantIso,
                langueId: this.languages.filter(l => l.nom === formData.language)[0].langueId,
                areas: this.areas.filter((area: Region) => formData.areas.includes(area.regionId)),
            };

            const groups: UserGroups = {
                adminGroups: [],
                expertabGroups: [],
                exploreGroups: formData.role
            };


            const areaIds = formData.areas;
            const siteIds = formData.sites;

            const userChanges: UserChanges = {
                user,
                groups,
                siteIds,
                areaIds
            };

            if (this.isEdit) {
                const userId = this.data.user.userId;
                const success = await this.userService.updateUser(userId, userChanges).toPromise();
                if (!success) {
                    throw new Error('Failed to update user');
                }
                this.dialogRef.close(user);
            } else {
                const createdUser = await this.userService.createUser(userChanges).toPromise();
                if (!createdUser) {
                    throw new Error('Failed to create user');
                }
                this.dialogRef.close(createdUser);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            const errorKey = this.isEdit ? 'SUBMISSION.UPDATE_ERROR' : 'SUBMISSION.CREATE_ERROR';
            this.errorMessage = this.translate.instant(errorKey);
        } finally {
            this.isLoading = false;
        }
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    resetForm(): void {
        this.userForm.reset({
            name: '',
            lastName: '',
            mail: '',
            language: '',
            role: '',
            company: '',
            mainOrganization: '',
            secondaryOrganizations: [],
            areas: [],
            adminAreas: false,
            sites: [],
            adminSites: false,
            active: true
        });
        this.errorMessage = null;
    }

    isFieldInvalid(field: string): boolean {
        const control = this.userForm.get(field);
        return control ? control.invalid && (control.touched || control.dirty) : false;
    }

    getFieldErrorMessage(field: string): string {
        const control = this.userForm.get(field);
        if (!control || !control.errors) return '';

        if (field === 'mail') {
            if (control.errors['required']) {
                return this.translate.instant('ERRORS.MAIL_REQUIRED');
            }
            if (control.errors['email']) {
                return this.translate.instant('ERRORS.INVALID_EMAIL');
            }
        }

        const errorKey = `ERRORS.${field.toUpperCase()}_REQUIRED`;
        return this.translate.instant(errorKey);
    }
}